import "@/index.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider } from "@/context/AuthContext";
import { ConfirmProvider } from "@/components/ConfirmProvider";
import ProtectedRoute from "@/components/ProtectedRoute";
import AdminRoute from "@/components/AdminRoute";
import PermRoute from "@/components/PermRoute";
import Layout from "@/components/Layout";
import LoginPage from "@/pages/LoginPage";
import DsePage from "@/pages/DsePage";
import GatePage from "@/pages/GatePage";
import DashboardPage from "@/pages/DashboardPage";
import InventoryPage from "@/pages/InventoryPage";
import InventoryCountPivotPage from "@/pages/InventoryCountPivotPage";
import SalesPage from "@/pages/SalesPage";
import CustomersPage from "@/pages/CustomersPage";
import CustomerDetailPage from "@/pages/CustomerDetailPage";
import PackagesPage from "@/pages/PackagesPage";
import BranchesPage from "@/pages/BranchesPage";
import BranchTypesPage from "@/pages/BranchTypesPage";
import ReportsPage from "@/pages/ReportsPage";
import AccountsPage from "@/pages/AccountsPage";
import ReceiptsPage from "@/pages/ReceiptsPage";
import FinanciersPage from "@/pages/FinanciersPage";
import EvaluatorsPage from "@/pages/EvaluatorsPage";
import VouchersPage from "@/pages/VouchersPage";
import ServicePage from "@/pages/ServicePage";
import BookingsPage from "@/pages/BookingsPage";
import SettingsPage from "@/pages/SettingsPage";
import UsersPage from "@/pages/UsersPage";
import RolesPage from "@/pages/RolesPage";
import AuditLogPage from "@/pages/AuditLogPage";
import SalesAgentsPage from "@/pages/SalesAgentsPage";
import ServiceStaffPage from "@/pages/ServiceStaffPage";
import SparePartsPage from "@/pages/SparePartsPage";
import SubDealersPage from "@/pages/SubDealersPage";
import RegistrationsPage from "@/pages/RegistrationsPage";
import ComplaintsPage from "@/pages/ComplaintsPage";
import ProfitPerVehicleReportPage from "@/pages/ProfitPerVehicleReportPage";
import ExtendedWarrantyReportPage from "@/pages/ExtendedWarrantyReportPage";
import ServicePartsOrdersReportPage from "@/pages/ServicePartsOrdersReportPage";
import ServiceInvoiceReportPage from "@/pages/ServiceInvoiceReportPage";
import GateRegisterReportPage from "@/pages/GateRegisterReportPage";
import RequiredOrderPage from "@/pages/RequiredOrderPage";
import AdminBulkClosePage from "@/pages/AdminBulkClosePage";
import AdminInvoiceRenumberPage from "@/pages/AdminInvoiceRenumberPage";
import CampaignsPage from "@/pages/CampaignsPage";
import AccessoriesPage from "@/pages/AccessoriesPage";
import PricingPage from "@/pages/PricingPage";
import QuotesPage from "@/pages/QuotesPage";
import SubDealerTxnTypesPage from "@/pages/SubDealerTxnTypesPage";
import BulkSalePage from "@/pages/BulkSalePage";
import PostSaleTasksPage from "@/pages/PostSaleTasksPage";
import HrEmployeesPage from "@/pages/HrEmployeesPage";
import HrAttendancePage from "@/pages/HrAttendancePage";
import HrLeavesPage from "@/pages/HrLeavesPage";
import HrHolidaysPage from "@/pages/HrHolidaysPage";
import HrAttendanceReportPage from "@/pages/HrAttendanceReportPage";
import HrPfEsiReportPage from "@/pages/HrPfEsiReportPage";
import PayrollPreviewPage from "@/pages/PayrollPreviewPage";
import TasksPage from "@/pages/TasksPage";
import { TOAST_OPTIONS } from "@/lib/constants";

function App() {
  return (
    <AuthProvider>
      <ConfirmProvider>
        <BrowserRouter>
        <Toaster position="top-right" toastOptions={TOAST_OPTIONS} />
        <Routes>
          <Route path="/login" element={<LoginPage />} />
          <Route path="/dse" element={<ProtectedRoute><DsePage /></ProtectedRoute>} />
          <Route path="/gate" element={<ProtectedRoute><GatePage /></ProtectedRoute>} />
          <Route path="/des" element={<Navigate to="/dse" replace />} />
          <Route
            path="/"
            element={
              <ProtectedRoute>
                <Layout />
              </ProtectedRoute>
            }
          >
            <Route index element={<DashboardPage />} />
            <Route path="inventory" element={<InventoryPage />} />
            <Route path="sales" element={<SalesPage />} />
            <Route path="sales/bulk" element={<BulkSalePage />} />
            <Route path="bookings" element={<BookingsPage />} />
            <Route path="customers" element={<CustomersPage />} />
            <Route path="customers/:id" element={<CustomerDetailPage />} />
            <Route path="sub-dealers" element={<SubDealersPage />} />
            <Route path="sub-dealers/:branchId" element={<SubDealersPage mode="detail" />} />
            <Route path="registrations" element={<RegistrationsPage />} />
            <Route path="post-sale-tasks" element={<PostSaleTasksPage />} />
            <Route path="complaints" element={<PermRoute perm="complaints.view"><ComplaintsPage /></PermRoute>} />
            <Route path="packages" element={<PermRoute perm="packages.view"><PackagesPage /></PermRoute>} />
            <Route path="branches" element={<PermRoute perm="branches.view"><BranchesPage /></PermRoute>} />
            <Route path="branch-types" element={<PermRoute perm="branch_types.manage"><BranchTypesPage /></PermRoute>} />
            <Route path="reports" element={<PermRoute perm="reports.view"><ReportsPage /></PermRoute>} />
            <Route path="reports/inventory-count-pivot" element={<PermRoute perm="reports.view"><InventoryCountPivotPage /></PermRoute>} />
            <Route path="reports/profit-per-vehicle" element={<PermRoute perm="reports.profit_per_vehicle"><ProfitPerVehicleReportPage /></PermRoute>} />
            <Route path="reports/extended-warranty" element={<PermRoute perm="reports.view"><ExtendedWarrantyReportPage /></PermRoute>} />
            <Route path="reports/service-parts-orders" element={<PermRoute perm={["reports.view", "service.view"]}><ServicePartsOrdersReportPage /></PermRoute>} />
            <Route path="reports/service-invoices" element={<PermRoute perm={["reports.view", "service.view"]}><ServiceInvoiceReportPage /></PermRoute>} />
            <Route path="reports/gate-register" element={<PermRoute perm={["service.reports.view", "service.reports", "reports.view"]}><GateRegisterReportPage /></PermRoute>} />
            <Route path="accounts" element={<PermRoute perm="accounts.view"><AccountsPage /></PermRoute>} />
            <Route path="receipts" element={<PermRoute perm="accounts.view"><ReceiptsPage /></PermRoute>} />
            <Route path="vouchers" element={<VouchersPage />} />
            <Route path="service" element={<PermRoute perm="service.view"><ServicePage /></PermRoute>} />
            <Route path="financiers" element={<PermRoute perm="financiers.manage"><FinanciersPage /></PermRoute>} />
            <Route path="evaluators" element={<PermRoute perm="evaluators.manage"><EvaluatorsPage /></PermRoute>} />
            <Route path="settings" element={<PermRoute perm="settings.view"><SettingsPage /></PermRoute>} />
            <Route path="users" element={<PermRoute perm="users.view"><UsersPage /></PermRoute>} />
            <Route path="roles" element={<PermRoute perm="roles.manage"><RolesPage /></PermRoute>} />
            <Route path="audit-log" element={<PermRoute perm="audit.view"><AuditLogPage /></PermRoute>} />
            <Route path="sales-agents" element={<AdminRoute><SalesAgentsPage /></AdminRoute>} />
            <Route path="admin/bulk-close" element={<AdminRoute><AdminBulkClosePage /></AdminRoute>} />
            <Route path="admin/invoice-renumber" element={<AdminRoute><AdminInvoiceRenumberPage /></AdminRoute>} />
            <Route path="service-staff" element={<AdminRoute><ServiceStaffPage /></AdminRoute>} />
            <Route path="hr/employees" element={<PermRoute perm="hr.view"><HrEmployeesPage /></PermRoute>} />
            <Route path="hr/attendance" element={<PermRoute perm="hr.view"><HrAttendancePage /></PermRoute>} />
            <Route path="hr/leaves" element={<PermRoute perm="hr.view"><HrLeavesPage /></PermRoute>} />
            <Route path="hr/holidays" element={<PermRoute perm="hr.view"><HrHolidaysPage /></PermRoute>} />
            <Route path="hr/reports/attendance" element={<PermRoute perm="hr.view"><HrAttendanceReportPage /></PermRoute>} />
            <Route path="hr/payroll" element={<PermRoute perm="hr.payroll.view"><PayrollPreviewPage /></PermRoute>} />
            <Route path="hr/reports/pf-esi" element={<PermRoute perm="hr.payroll.view"><HrPfEsiReportPage /></PermRoute>} />
            <Route path="tasks" element={<TasksPage />} />
            <Route path="spare-parts" element={<PermRoute perm="service.view"><RequiredOrderPage /></PermRoute>} />
            <Route path="spare-parts/stock" element={<PermRoute perm="service.view"><SparePartsPage /></PermRoute>} />
            <Route path="campaigns" element={<PermRoute perm={["campaigns.view", "campaigns.create"]}><CampaignsPage /></PermRoute>} />
            <Route path="accessories" element={<PermRoute perm="accessories.view"><AccessoriesPage /></PermRoute>} />
            <Route path="pricing" element={<PermRoute perm="pricing.manage"><PricingPage /></PermRoute>} />
            <Route path="quotes" element={<PermRoute perm="quotes.view"><QuotesPage /></PermRoute>} />
            <Route path="sub-dealers/transaction-types" element={<AdminRoute><SubDealerTxnTypesPage /></AdminRoute>} />
          </Route>
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
      </ConfirmProvider>
    </AuthProvider>
  );
}

export default App;
